import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown, Plus, Pencil, Trash2, ArrowRight, Check, X, Loader2, AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export const Action = {
  Create: 'Create',
  Update: 'Update',
  Delete: 'Delete',
} as const;

export type ActionType = (typeof Action)[keyof typeof Action];

export interface ConfigAction {
  field: string;
  key: string;
  action: ActionType;
  currentValue?: string | number | boolean | null;
  proposedValue: string | number | boolean;
  description?: string;
  warning?: string;
}

export interface ConfigActionGroup {
  groupName: string;
  key: string;
  actions: ConfigAction[];
  description?: string;
  warning?: string;
}

export interface ApplyResult {
  changeId: string;
  success: boolean;
  error?: string;
}

interface ConfigurationPlanProps {
  groups: ConfigActionGroup[];
  selectedActions: Map<string, Set<string>>;
  onSelectionChange: (groupName: string, field: string, selected: boolean) => void;
  onGroupSelectionChange: (groupName: string, selected: boolean) => void;
  isApplying?: boolean;
  applyResults?: ApplyResult[];
  disabled?: boolean;
}

const actionConfig: Record<
  ActionType,
  { icon: typeof Plus; color: string; bgColor: string; label: string }
> = {
  [Action.Create]: {
    icon: Plus,
    color: 'text-green-400',
    bgColor: 'bg-green-400/10',
    label: 'create',
  },
  [Action.Update]: {
    icon: Pencil,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-400/10',
    label: 'update',
  },
  [Action.Delete]: {
    icon: Trash2,
    color: 'text-red-400',
    bgColor: 'bg-red-400/10',
    label: 'delete',
  },
};

function formatValue(value: string | number | boolean | null | undefined): React.ReactNode {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'boolean') {
    return <span className={value ? 'text-green-400' : 'text-red-400'}>{value ? 'true' : 'false'}</span>;
  }
  if (typeof value === 'string') return `"${value}"`;
  return String(value);
}

interface ActionRowProps {
  action: ConfigAction;
  isSelected: boolean;
  onSelect: (selected: boolean) => void;
  isApplying?: boolean;
  applyResult?: ApplyResult;
  disabled?: boolean;
}

function ActionRow({ action, isSelected, onSelect, isApplying, applyResult, disabled }: ActionRowProps) {
  const config = actionConfig[action.action];
  const Icon = config.icon;

  return (
    <div
      className={cn(
        'rounded-lg border border-white/5 p-3 transition-colors',
        isSelected ? 'bg-white/5' : 'bg-transparent hover:bg-white/[0.02]',
        applyResult?.success && 'border-green-500/30 bg-green-500/5',
        applyResult && !applyResult.success && 'border-orange-500/30 bg-orange-500/5'
      )}
    >
      <div className="flex items-center gap-3">
        {isApplying ? (
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        ) : applyResult ? (
          applyResult.success ? (
            <Check className="h-5 w-5 text-green-400" />
          ) : (
            <X className="h-5 w-5 text-orange-400" />
          )
        ) : (
          <Checkbox
            checked={isSelected}
            disabled={disabled}
            onCheckedChange={(checked) => {
              onSelect(checked === true);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <div className={cn('flex items-center gap-2 rounded px-2 py-1', config.bgColor)}>
          <Icon className={cn('h-3 w-3', config.color)} />
          <span className={cn('text-xs font-medium', config.color)}>{config.label}</span>
        </div>
        <span className="font-mono text-sm text-white">{action.field}</span>
        <div className="ml-auto flex items-center gap-2 font-mono text-sm">
          {action.action === Action.Create ? (
            <span className="text-green-400">{formatValue(action.proposedValue)}</span>
          ) : action.action === Action.Delete ? (
            <span className="text-gray-400">{formatValue(action.currentValue)}</span>
          ) : (
            <>
              {action.currentValue !== null && action.currentValue !== undefined && (
                <span className="text-gray-400">{formatValue(action.currentValue)}</span>
              )}
              {action.currentValue !== null && action.currentValue !== undefined && 
               action.proposedValue !== null && action.proposedValue !== undefined && (
                <ArrowRight className="h-4 w-4 text-gray-500" />
              )}
              {action.proposedValue !== null && action.proposedValue !== undefined && (
                <span className="text-green-400">{formatValue(action.proposedValue)}</span>
              )}
            </>
          )}
        </div>
        {applyResult && !applyResult.success && applyResult.error && (
          <span className="text-xs text-orange-400">{applyResult.error}</span>
        )}
      </div>
      {action.description && (
        <div className="mt-2 flex items-start gap-2 text-xs text-gray-400 pl-8">
          <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>{action.description}</span>
        </div>
      )}
      {action.warning && (
        <div className="mt-2 flex items-start gap-2 rounded-md bg-yellow-500/10 p-2 text-xs text-yellow-400 ml-8">
          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>{action.warning}</span>
        </div>
      )}
    </div>
  );
}

interface ActionGroupProps {
  group: ConfigActionGroup;
  selectedFields: Set<string>;
  onActionSelect: (field: string, selected: boolean) => void;
  onGroupSelect: (selected: boolean) => void;
  isApplying?: boolean;
  applyResults?: ApplyResult[];
  disabled?: boolean;
}

function ActionGroup({
  group,
  selectedFields,
  onActionSelect,
  onGroupSelect,
  isApplying,
  applyResults,
  disabled,
}: ActionGroupProps) {
  const [isOpen, setIsOpen] = useState(true);

  const allSelected = group.actions.every((a) => selectedFields.has(a.key));
  const someSelected = group.actions.some((a) => selectedFields.has(a.key));
  const checkboxState = allSelected ? true : someSelected ? 'indeterminate' : false;

  const actionCounts = useMemo(() => {
    const counts = { create: 0, update: 0, delete: 0 };
    group.actions.forEach((action) => {
      if (action.action === Action.Create) counts.create++;
      else if (action.action === Action.Update) counts.update++;
      else if (action.action === Action.Delete) counts.delete++;
    });
    return counts;
  }, [group.actions]);

  // Compute group-level result status
  const groupResultStatus = useMemo(() => {
    if (!applyResults || applyResults.length === 0) return null;
    
    const groupResults = group.actions
      .map((action) => {
        const changeId = `${group.key}.${action.key}`;
        return applyResults.find((r) => r.changeId === changeId);
      })
      .filter((r): r is ApplyResult => r !== undefined);
    
    if (groupResults.length === 0) return null;
    
    const successCount = groupResults.filter((r) => r.success).length;
    const failCount = groupResults.filter((r) => !r.success).length;
    
    return {
      hasResults: true,
      allSuccess: failCount === 0,
      allFail: successCount === 0,
      successCount,
      failCount,
      total: groupResults.length,
    };
  }, [applyResults, group.actions, group.key]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={cn(
      "rounded-xl border bg-card2",
      groupResultStatus?.allSuccess && "border-green-500/30",
      groupResultStatus?.hasResults && !groupResultStatus.allSuccess && "border-orange-500/30",
      !groupResultStatus?.hasResults && "border-white/10"
    )}>
      <CollapsibleTrigger className="flex w-full items-center gap-3 p-4 text-left hover:bg-white/5">
        {isApplying ? (
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        ) : groupResultStatus?.hasResults ? (
          groupResultStatus.allSuccess ? (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500/20">
              <Check className="h-3 w-3 text-green-400" />
            </div>
          ) : (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-orange-500/20">
              <X className="h-3 w-3 text-orange-400" />
            </div>
          )
        ) : (
          <Checkbox
            checked={checkboxState}
            disabled={disabled}
            onCheckedChange={() => {
              // When indeterminate or fully unchecked, select all
              // When fully checked, deselect all
              const shouldSelectAll = checkboxState !== true;
              onGroupSelect(shouldSelectAll);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <ChevronDown
          className={cn(
            'h-4 w-4 text-gray-400 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
        <span className="font-semibold text-white">{group.groupName}</span>
        <div className="ml-auto flex items-center gap-3 text-xs">
          {groupResultStatus?.hasResults ? (
            <>
              <span className="text-green-400">{groupResultStatus.successCount} succeeded</span>
              {groupResultStatus.failCount > 0 && (
                <span className="text-orange-400">{groupResultStatus.failCount} failed</span>
              )}
            </>
          ) : (
            <>
              {actionCounts.create > 0 && (
                <span className="text-green-400">+{actionCounts.create}</span>
              )}
              {actionCounts.update > 0 && (
                <span className="text-yellow-400">~{actionCounts.update}</span>
              )}
              {actionCounts.delete > 0 && (
                <span className="text-red-400">-{actionCounts.delete}</span>
              )}
              <span className="text-gray-500">
                {selectedFields.size}/{group.actions.length} selected
              </span>
            </>
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 px-4 pb-4">
        {group.warning && (
          <div className="flex items-start gap-2 rounded-md bg-yellow-500/10 p-3 text-sm text-yellow-400">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{group.warning}</span>
          </div>
        )}
        {group.description && (
          <div className="flex items-start gap-2 text-sm text-gray-400 mb-2">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{group.description}</span>
          </div>
        )}
        {group.actions.map((action) => {
          // Use key properties for matching with backend changeIds (e.g., "smtp.enabled")
          const changeId = `${group.key}.${action.key}`;
          const result = applyResults?.find((r) => r.changeId === changeId);
          return (
            <ActionRow
              key={action.key}
              action={action}
              isSelected={selectedFields.has(action.key)}
              onSelect={(selected) => onActionSelect(action.key, selected)}
              isApplying={isApplying}
              applyResult={result}
              disabled={disabled}
            />
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function ConfigurationPlan({
  groups,
  selectedActions,
  onSelectionChange,
  onGroupSelectionChange,
  isApplying,
  applyResults,
  disabled,
}: ConfigurationPlanProps) {
  const totalStats = useMemo(() => {
    let create = 0;
    let update = 0;
    let deleteCount = 0;
    let selectedCount = 0;
    let totalCount = 0;

    groups.forEach((group) => {
      const selected = selectedActions.get(group.groupName) ?? new Set();
      group.actions.forEach((action) => {
        totalCount++;
        if (selected.has(action.key)) {
          selectedCount++;
          if (action.action === Action.Create) create++;
          else if (action.action === Action.Update) update++;
          else if (action.action === Action.Delete) deleteCount++;
        }
      });
    });

    return { create, update, delete: deleteCount, selectedCount, totalCount };
  }, [groups, selectedActions]);

  // Compute apply results summary
  const resultsSummary = useMemo(() => {
    if (!applyResults || applyResults.length === 0) return null;
    
    const successCount = applyResults.filter((r) => r.success).length;
    const failCount = applyResults.filter((r) => !r.success).length;
    const failures = applyResults.filter((r) => !r.success);
    
    return {
      total: applyResults.length,
      successCount,
      failCount,
      allSuccess: failCount === 0,
      failures,
    };
  }, [applyResults]);

  if (groups.length === 0) {
    return (
      <div className="rounded-xl border border-white/10 bg-card2 p-8 text-center">
        <p className="text-gray-400">No configuration changes detected</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {groups.map((group) => (
          <ActionGroup
            key={group.groupName}
            group={group}
            selectedFields={selectedActions.get(group.groupName) ?? new Set()}
            onActionSelect={(field, selected) =>
              onSelectionChange(group.groupName, field, selected)
            }
            onGroupSelect={(selected) => onGroupSelectionChange(group.groupName, selected)}
            isApplying={isApplying}
            applyResults={applyResults}
            disabled={disabled}
          />
        ))}
      </div>
      
      {/* Results Summary */}
      {resultsSummary && (
        <div className={cn(
          "rounded-xl border p-4",
          resultsSummary.allSuccess 
            ? "border-green-500/30 bg-green-500/5" 
            : "border-orange-500/30 bg-orange-500/5"
        )}>
          <div className="flex items-center gap-3 mb-3">
            {resultsSummary.allSuccess ? (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500/20">
                <Check className="h-4 w-4 text-green-400" />
              </div>
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500/20">
                <AlertTriangle className="h-4 w-4 text-orange-400" />
              </div>
            )}
            <div>
              <h4 className={cn(
                "font-semibold",
                resultsSummary.allSuccess ? "text-green-400" : "text-orange-400"
              )}>
                {resultsSummary.allSuccess 
                  ? "All changes applied successfully" 
                  : "Some changes failed to apply"}
              </h4>
              <p className="text-sm text-gray-400">
                {resultsSummary.successCount} of {resultsSummary.total} changes succeeded
                {resultsSummary.failCount > 0 && `, ${resultsSummary.failCount} failed`}
              </p>
            </div>
          </div>
          
          {resultsSummary.failures.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-sm font-medium text-orange-400">Failed changes:</p>
              {resultsSummary.failures.map((failure) => (
                <div 
                  key={failure.changeId} 
                  className="flex items-start gap-2 rounded-md bg-orange-500/10 p-2 text-sm"
                >
                  <X className="h-4 w-4 text-orange-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-mono text-orange-300">{failure.changeId}</span>
                    {failure.error && (
                      <p className="text-orange-400/70 text-xs mt-1">{failure.error}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Hook for managing selection state
export function useConfigurationPlanSelection(groups: ConfigActionGroup[]) {
  const [selectedActions, setSelectedActions] = useState<Map<string, Set<string>>>(new Map());

  // Initialize selection when groups change (e.g., after API fetch)
  useEffect(() => {
    if (groups.length > 0) {
      const initial = new Map<string, Set<string>>();
      groups.forEach((group) => {
        initial.set(group.groupName, new Set(group.actions.map((a) => a.key)));
      });
      setSelectedActions(initial);
    }
  }, [groups]);

  const handleSelectionChange = useCallback(
    (groupName: string, key: string, selected: boolean) => {
      setSelectedActions((prev) => {
        const next = new Map(prev);
        const groupSet = new Set(next.get(groupName) ?? []);
        if (selected) {
          groupSet.add(key);
        } else {
          groupSet.delete(key);
        }
        next.set(groupName, groupSet);
        return next;
      });
    },
    []
  );

  const handleGroupSelectionChange = useCallback(
    (groupName: string, selected: boolean) => {
      setSelectedActions((prev) => {
        const next = new Map(prev);
        const group = groups.find((g) => g.groupName === groupName);
        if (group) {
          if (selected) {
            next.set(groupName, new Set(group.actions.map((a) => a.key)));
          } else {
            next.set(groupName, new Set());
          }
        }
        return next;
      });
    },
    [groups]
  );

  const getSelectedActions = useCallback((): ConfigAction[] => {
    const result: ConfigAction[] = [];
    groups.forEach((group) => {
      const selected = selectedActions.get(group.groupName) ?? new Set();
      group.actions.forEach((action) => {
        if (selected.has(action.key)) {
          result.push(action);
        }
      });
    });
    return result;
  }, [groups, selectedActions]);

  return {
    selectedActions,
    handleSelectionChange,
    handleGroupSelectionChange,
    getSelectedActions,
  };
}
